import { LOAD_NUMBER_SMALL } from 'config'
import { yayApi } from '../baseApi'
import {
  Category,
  CurrencyRate,
  Empl,
  EmplTransaction,
  GifCategory,
  PresignedUrl,
  StickerPack,
  User,
  WalletEmplTranasactionDetailsQueryResponse,
  WalletSource,
  WalletTransactionDetailsQueryResponse,
  YayApiResponse,
  VerifyDotMoneyParams,
} from 'lib/_types'
import {
  pathApiDotMoneyCallbackUrl,
  pathApiExpiringEmplTransactions,
  pathApiPresignedTestnetAirdrop,
  pathApiPresignedWalletBagCreate,
  pathApiPresignedWalletBagShow,
  pathApiPresignedWalletCurrency,
  pathApiPresignedWalletEmplTransactionDetails,
  pathApiPresignedWalletEmplTransactions,
  pathApiPresignedWalletExpiringEmplTransactions,
  pathApiPresignedWalletLink,
  pathApiPresignedWalletShow,
  pathApiPresignedWalletTransactionDetails,
  pathApiPresignedWalletUnlink,
  pathApiStickerPacks,
  pathApiUsersGifData,
  pathApiUsersRanking,
} from 'lib/paths'
import { axiosBaseQuery, axiosShortQuery } from 'store/libs/baseQuery'

export const miscApi = yayApi.injectEndpoints({
  endpoints: (build) => ({
    getGenresList: build.query<
      { genres: Category[] },
      { number?: number; fromId?: number }
    >({
      query: ({ number = LOAD_NUMBER_SMALL, fromId }) => ({
        url: '/v1/genres',
        params: {
          number,
          fromId,
        },
      }),
    }),

    bucketsPresignedUrls: build.mutation<
      {
        presigned_urls: Array<{
          filename: string
          url: string
        }>
      },
      string
    >({
      query: (params) => ({
        url: `/v1/buckets/presigned_urls?${params}`,
        method: 'GET',
      }),
    }),

    getStickers: build.query<
      {
        sticker_packs: Array<StickerPack>
      },
      void
    >({
      query: () => ({
        url: pathApiStickerPacks,
      }),
    }),

    getGifs: build.query<
      {
        gif_categories: Array<GifCategory>
      },
      void
    >({
      query: () => ({
        url: pathApiUsersGifData,
      }),
    }),

    getUsersRanking: build.query<
      {
        users: Array<User>
      },
      {
        mode: 'all_time' | 'six_months' | 'one_month'
      }
    >({
      query: ({ mode }) => ({
        url: pathApiUsersRanking,
        params: {
          mode,
        },
      }),
      keepUnusedDataFor: 0,
    }),

    testnetAirdrop: build.mutation<
      { result: { claim_id: string; delivered: boolean } },
      { user_id: number; email: string; wallet_address: string }
    >({
      queryFn: async (params, baseQueryApi) => {
        const res = await axiosBaseQuery<YayApiResponse<PresignedUrl>>({
          client: 'default',
        })(
          {
            url: pathApiPresignedTestnetAirdrop,
            method: 'GET',
            params,
          },
          baseQueryApi
        )

        const url = 'data' in res && res.data.presigned_url

        // something wrong
        if (!url) return res

        const res2 = await axiosBaseQuery({ client: 'free' })(
          { url, method: 'POST' },
          baseQueryApi
        )

        return res2
      },
    }),

    walletBagPreCreate: build.mutation({
      queryFn: async (_, baseQueryApi) => {
        const getBagPresigned = await axiosBaseQuery<
          YayApiResponse<PresignedUrl>
        >({ client: 'default' })(
          { url: pathApiPresignedWalletBagShow, method: 'GET' },
          baseQueryApi
        )

        const getBagPresignedUrl =
          'data' in getBagPresigned && getBagPresigned.data.presigned_url

        // something wrong
        if (!getBagPresignedUrl) return getBagPresigned

        const getBag = await axiosBaseQuery({ client: 'free' })(
          { url: getBagPresignedUrl, method: 'GET' },
          baseQueryApi
        )

        // already have bag
        if ('data' in getBag) return getBag

        // something wrong
        if ('error' in getBag && getBag.error.status !== 404) return getBag

        // create bag
        const createBagPresigned = await axiosBaseQuery<
          YayApiResponse<PresignedUrl>
        >({ client: 'default' })(
          { url: pathApiPresignedWalletBagCreate, method: 'GET' },
          baseQueryApi
        )

        const createBagPresignedUrl =
          'data' in createBagPresigned && createBagPresigned.data.presigned_url

        // something wrong
        if (!createBagPresignedUrl) return createBagPresigned

        const createBag = await axiosBaseQuery({ client: 'free' })(
          { url: createBagPresignedUrl, method: 'POST' },
          baseQueryApi
        )

        return createBag
      },
    }),

    getEmplExpiringSoon: build.query<
      {
        rewarded_expiring: Array<{
          expiring_amount: string
          expiring_date: number
        }>
        regular_expiring: Array<{
          expiring_amount: string
          expiring_date: number
        }>
      },
      { limit?: number }
    >({
      query: ({ limit = 3 }) => ({
        url: pathApiExpiringEmplTransactions,
        params: {
          limit,
        },
      }),
    }),

    getCurrencyRate: build.mutation<
      {
        result: {
          ETH: CurrencyRate
          YAY: CurrencyRate
          EMPL: CurrencyRate
        }
      },
      void
    >({
      queryFn: async (_, baseQueryApi) => {
        const currencyPresigned = await axiosBaseQuery<
          YayApiResponse<PresignedUrl>
        >({ client: 'default' })(
          {
            url: pathApiPresignedWalletCurrency,
            method: 'GET',
          },
          baseQueryApi
        )

        const getCurrencyRatePresignedUrl =
          'data' in currencyPresigned && currencyPresigned.data.presigned_url

        // something wrong
        if (!getCurrencyRatePresignedUrl) return currencyPresigned as any

        const getCurrencyRate = await axiosBaseQuery<{
          result: {
            ETH: CurrencyRate
            YAY: CurrencyRate
            EMPL: CurrencyRate
          }
        }>({
          client: 'free',
        })(
          {
            url: getCurrencyRatePresignedUrl,
            method: 'GET',
          },
          baseQueryApi
        )

        return getCurrencyRate
      },
    }),

    getWalletExpiringEmpls: build.mutation<
      {
        cursor?: string
        expirations: Array<any>
        total_amount: number
      },
      {
        emplType: string
      }
    >({
      queryFn: async ({ emplType }, baseQueryApi) => {
        const expiriesPresigned = await axiosBaseQuery<
          YayApiResponse<PresignedUrl>
        >({})(
          {
            url: pathApiPresignedWalletExpiringEmplTransactions,
            method: 'GET',
          },
          baseQueryApi
        )

        const getExpiriesPresignedUrl =
          'data' in expiriesPresigned && expiriesPresigned.data.presigned_url

        // something wrong
        if (!getExpiriesPresignedUrl) return expiriesPresigned as any

        const getExpiriesList = await axiosBaseQuery<{
          cursor?: string
          expirations: Array<any>
          total_amount: number
        }>({})(
          {
            url: getExpiriesPresignedUrl,
            method: 'GET',
            params: { type: emplType },
          },
          baseQueryApi
        )

        return getExpiriesList
      },
    }),

    getWallet: build.mutation<
      { result: { wallet_address: string } },
      { source: WalletSource }
    >({
      queryFn: async ({ source }, baseQueryApi) => {
        const getWalletPresigned = await axiosBaseQuery<
          YayApiResponse<PresignedUrl>
        >({ client: 'default' })(
          {
            url: pathApiPresignedWalletShow,
            method: 'GET',
            params: { source },
          },
          baseQueryApi
        )

        const getWalletPresignedUrl =
          'data' in getWalletPresigned && getWalletPresigned.data.presigned_url

        // something wrong
        if (!getWalletPresignedUrl) return getWalletPresigned as any

        const getWallet = await axiosBaseQuery<{
          result: { wallet_address: string }
        }>({
          client: 'free',
        })(
          { url: getWalletPresignedUrl, method: 'GET', params: { source } },
          baseQueryApi
        )

        if ('error' in getWallet && getWallet.error.status === 404)
          return {
            data: { result: { wallet_address: '' } },
          }

        return getWallet
      },
    }),

    getWalletEmplTransactions: build.mutation<
      {
        result: Array<EmplTransaction>
        cursor: string | null
      },
      { cursor?: string; size?: number }
    >({
      queryFn: async ({ cursor, size }, baseQueryApi) => {
        const getWalletEmplTransactionsPresigned = await axiosBaseQuery<
          YayApiResponse<PresignedUrl>
        >({ client: 'default' })(
          {
            url: pathApiPresignedWalletEmplTransactions,
            method: 'GET',
            params: { cursor, size },
          },
          baseQueryApi
        )

        const getWalletEmplTransactionsPresignedUrl =
          'data' in getWalletEmplTransactionsPresigned &&
          getWalletEmplTransactionsPresigned.data.presigned_url

        // something wrong
        if (!getWalletEmplTransactionsPresignedUrl)
          return getWalletEmplTransactionsPresigned as any

        const getWalletEmplTransactions = await axiosBaseQuery<{
          result: Array<EmplTransaction>
          cursor: string | null
        }>({
          client: 'free',
        })(
          {
            url: getWalletEmplTransactionsPresignedUrl,
            method: 'GET',
            params: { cursor, size },
          },
          baseQueryApi
        )

        if (
          'error' in getWalletEmplTransactions &&
          getWalletEmplTransactions.error.status === 404
        ) {
          return { data: { result: [], cursor: null } }
        }

        return getWalletEmplTransactions
      },
    }),

    getWalletEmplTransactionDetails: build.mutation<
      WalletEmplTranasactionDetailsQueryResponse,
      { id: string }
    >({
      queryFn: async ({ id }, baseQueryApi) => {
        const getWalletEmplTransactionDetailsPresigned = await axiosBaseQuery<
          YayApiResponse<PresignedUrl>
        >({ client: 'default' })(
          {
            url: pathApiPresignedWalletEmplTransactionDetails,
            method: 'GET',
            params: { id },
          },
          baseQueryApi
        )

        const getWalletEmplTransactionDetailsPresignedUrl =
          'data' in getWalletEmplTransactionDetailsPresigned &&
          getWalletEmplTransactionDetailsPresigned.data.presigned_url

        // something wrong
        if (!getWalletEmplTransactionDetailsPresignedUrl)
          return getWalletEmplTransactionDetailsPresigned as any

        const getWalletEmplTransactionDetails =
          await axiosBaseQuery<WalletEmplTranasactionDetailsQueryResponse>({
            client: 'free',
          })(
            {
              url: getWalletEmplTransactionDetailsPresignedUrl,
              method: 'GET',
            },
            baseQueryApi
          )

        if (
          'error' in getWalletEmplTransactionDetails &&
          getWalletEmplTransactionDetails.error.status === 404
        ) {
          return { data: { result: [], cursor: null } }
        }

        return getWalletEmplTransactionDetails
      },
    }),

    getWalletTransactionDetails: build.mutation<
      WalletTransactionDetailsQueryResponse,
      { transactionCode: string }
    >({
      queryFn: async ({ transactionCode }, baseQueryApi) => {
        const getWalletTransactionDetailsPresigned = await axiosBaseQuery<
          YayApiResponse<PresignedUrl>
        >({ client: 'default' })(
          {
            url: pathApiPresignedWalletTransactionDetails,
            method: 'GET',
            params: { transaction_code: transactionCode },
          },
          baseQueryApi
        )

        const getWalletTransactionDetailsPresignedUrl =
          'data' in getWalletTransactionDetailsPresigned &&
          getWalletTransactionDetailsPresigned.data.presigned_url

        // something wrong
        if (!getWalletTransactionDetailsPresignedUrl)
          return getWalletTransactionDetailsPresigned as any

        const getWalletTransactionDetails =
          await axiosBaseQuery<WalletTransactionDetailsQueryResponse>({
            client: 'free',
          })(
            {
              url: getWalletTransactionDetailsPresignedUrl,
              method: 'GET',
            },
            baseQueryApi
          )

        if (
          'error' in getWalletTransactionDetails &&
          getWalletTransactionDetails.error.status === 404
        ) {
          return { data: { result: {} } }
        }

        return getWalletTransactionDetails
      },
    }),

    walletLink: build.mutation<
      YayApiResponse<PresignedUrl>,
      { wallet_address: string; source: WalletSource }
    >({
      query: ({ wallet_address, source }) => ({
        url: pathApiPresignedWalletLink,
        params: { wallet_address, source },
        method: 'GET',
      }),
    }),

    walletUnlink: build.mutation<
      YayApiResponse,
      { reason: 'user_delete' | 'user_unlink'; source: WalletSource }
    >({
      queryFn: async ({ reason, source }, baseQueryApi) => {
        const unlinkPresigned = await axiosBaseQuery<
          YayApiResponse<PresignedUrl>
        >({ client: 'default' })(
          {
            url: pathApiPresignedWalletUnlink,
            method: 'GET',
            params: { reason, source },
          },
          baseQueryApi
        )

        const unlinkPresignedUrl =
          'data' in unlinkPresigned && unlinkPresigned.data.presigned_url

        // something wrong
        if (!unlinkPresignedUrl) return unlinkPresigned

        const unlink = await axiosBaseQuery<YayApiResponse>({
          client: 'free',
        })(
          {
            url: unlinkPresignedUrl,
            method: 'DELETE',
            params: { source: source },
          },
          baseQueryApi
        )

        return unlink
      },
    }),

    dotMoneyLoginUrl: build.query<
      {
        url: string
      },
      {
        callback_url: string
      }
    >({
      query: ({ callback_url }) => ({
        url: pathApiDotMoneyCallbackUrl,
        params: { callback_url },
      }),
    }),

    dotMoneyVerify: build.mutation<
      {
        transaction_code: string
      },
      VerifyDotMoneyParams
    >({
      queryFn: axiosShortQuery(
        (params) => ({
          url: '/api/verify_dotmoney_exchange',
          method: 'POST',
          data: params,
        }),
        'self'
      ),
    }),

    dotMoneyGetTransactionStatus: build.query<
      {
        url: string
      },
      {
        transactionId: string
      }
    >({
      queryFn: axiosShortQuery(
        ({ transactionId }) => ({
          url: `/api/verify_dotmoney_transaction_status/${transactionId}`,
          method: 'GET',
        }),
        'self'
      ),
    }),
  }),

  overrideExisting: false,
})

export const {
  useGetGenresListQuery,
  useBucketsPresignedUrlsMutation,
  useGetStickersQuery,
  useGetGifsQuery,
  useGetUsersRankingQuery,
  useTestnetAirdropMutation,
  useGetCurrencyRateMutation,
  useWalletBagPreCreateMutation,
  useGetWalletMutation,
  useGetWalletEmplTransactionsMutation,
  useGetWalletEmplTransactionDetailsMutation,
  useGetWalletTransactionDetailsMutation,
  useWalletLinkMutation,
  useWalletUnlinkMutation,
  useDotMoneyVerifyMutation,
  useLazyDotMoneyLoginUrlQuery,
  useLazyDotMoneyGetTransactionStatusQuery,
  useGetEmplExpiringSoonQuery,
  useGetWalletExpiringEmplsMutation,
} = miscApi
